import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Heart, Clock, CheckCircle, XCircle, AlertCircle, Search, Eye, Home, ArrowRight, MessageSquare, Send, Bell, Filter, Trash2, BadgeCheck, Briefcase, Building2, Calculator, HardHat, CheckCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

function ApplicationStatusCard({ title, status, reviewerNotes }: { title: string; status: string; reviewerNotes?: string | null }) {
  const config: Record<string, { icon: any; color: string; label: string }> = {
    pending: { icon: Clock, color: 'text-yellow-600', label: 'Pending Review' },
    approved: { icon: CheckCircle, color: 'text-green-600', label: 'Approved' },
    rejected: { icon: XCircle, color: 'text-destructive', label: 'Rejected' },
  };
  const c = config[status] || config.pending;
  const Icon = c.icon;

  return (
    <Card className="border-l-4" style={{ borderLeftColor: status === 'approved' ? 'hsl(var(--primary))' : status === 'rejected' ? 'hsl(var(--destructive))' : '#ca8a04' }}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold">{title}</span>
          <Badge variant={status === 'approved' ? 'default' : status === 'rejected' ? 'destructive' : 'secondary'} className="gap-1">
            <Icon className="h-3 w-3" />{c.label}
          </Badge>
        </div>
        {status === 'rejected' && reviewerNotes && (
          <div className="flex items-start gap-2 bg-destructive/10 rounded-lg p-3 text-sm">
            <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
            <span>{reviewerNotes}</span>
          </div>
        )}
        {status === 'pending' && (
          <p className="text-xs text-muted-foreground">Your application is being reviewed by our team. This usually takes 1-3 business days.</p>
        )}
      </CardContent>
    </Card>
  );
}

const leadStatusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  new: { label: 'Sent', variant: 'secondary' },
  contacted: { label: 'In Contact', variant: 'default' },
  converted: { label: 'Successful', variant: 'default' },
  closed: { label: 'Closed', variant: 'outline' },
};

export function UserDashboard() {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  // Saved properties count
  const { data: savedCount } = useQuery({
    queryKey: ['saved-count', user?.id],
    queryFn: async () => {
      const { count } = await supabase.from('favorites').select('*', { count: 'exact', head: true }).eq('user_id', user!.id);
      return count || 0;
    },
    enabled: !!user,
  });

  // Recent favorites
  const { data: recentFavorites } = useQuery({
    queryKey: ['recent-favorites', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('favorites')
        .select('*, properties(title, city, price, currency, property_type, listing_type)')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(5);
      return data || [];
    },
    enabled: !!user,
  });

  // My inquiries (leads I sent)
  const { data: myInquiries } = useQuery({
    queryKey: ['my-inquiries', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('leads')
        .select('id, name, message, status, created_at, property_id, project_id, properties(title, city), projects(name, city)')
        .eq('sender_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(10);
      return data || [];
    },
    enabled: !!user,
  });

  // Unread notifications count
  const { data: unreadNotifications } = useQuery({
    queryKey: ['unread-notifications-count', user?.id],
    queryFn: async () => {
      const { count } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user!.id)
        .eq('is_read', false);
      return count || 0;
    },
    enabled: !!user,
  });

  // Recent notifications
  const { data: recentNotifications } = useQuery({
    queryKey: ['recent-notifications', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(5);
      return data || [];
    },
    enabled: !!user,
  });

  // Recently viewed properties
  const { data: recentViews } = useQuery({
    queryKey: ['recent-views', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('property_views')
        .select('id, viewed_at, property_id, properties(title, city, price, currency, property_type)')
        .eq('user_id', user!.id)
        .order('viewed_at', { ascending: false })
        .limit(5);
      return data || [];
    },
    enabled: !!user,
  });

  // Saved searches
  const { data: savedSearches } = useQuery({
    queryKey: ['saved-searches', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('saved_searches')
        .select('*')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(5);
      return data || [];
    },
    enabled: !!user,
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user!.id)
        .eq('is_read', false);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['unread-notifications-count'] });
      qc.invalidateQueries({ queryKey: ['recent-notifications'] });
      toast.success('All notifications marked as read');
    },
    onError: (e: any) => toast.error(e.message || 'Failed'),
  });

  const deleteSavedSearch = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('saved_searches').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['saved-searches'] });
      toast.success('Saved search deleted');
    },
    onError: (e: any) => toast.error(e.message || 'Failed'),
  });

  const toggleSearchAlerts = useMutation({
    mutationFn: async ({ id, notify }: { id: string; notify: boolean }) => {
      const { error } = await supabase
        .from('saved_searches')
        .update({ notify_email: notify })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['saved-searches'] });
    },
    onError: (e: any) => toast.error(e.message || 'Failed'),
  });

  // Applications
  const { data: applications } = useQuery({
    queryKey: ['my-applications', user?.id],
    queryFn: async () => {
      const results: { title: string; status: string; reviewerNotes?: string | null }[] = [];
      const { data: devApp } = await supabase.from('developer_applications').select('status, reviewer_notes').eq('user_id', user!.id).order('created_at', { ascending: false }).limit(1);
      if (devApp?.length) results.push({ title: 'Developer Application', status: devApp[0].status, reviewerNotes: devApp[0].reviewer_notes });
      const { data: agentApp } = await supabase.from('agent_applications').select('status, reviewer_notes').eq('user_id', user!.id).order('created_at', { ascending: false }).limit(1);
      if (agentApp?.length) results.push({ title: 'Agent Application', status: agentApp[0].status, reviewerNotes: agentApp[0].reviewer_notes });
      const { data: agencyApp } = await supabase.from('agency_applications').select('status, reviewer_notes').eq('user_id', user!.id).order('created_at', { ascending: false }).limit(1);
      if (agencyApp?.length) results.push({ title: 'Agency Application', status: agencyApp[0].status, reviewerNotes: agencyApp[0].reviewer_notes });
      return results;
    },
    enabled: !!user,
  });

  const inquiryCount = myInquiries?.length || 0;
  const isProfessional = hasRole('agent') || hasRole('agency_admin') || hasRole('developer');
  const hasPendingApplication = (applications || []).some(a => a.status === 'pending');
  const showBecomeProCTA = !isProfessional && !hasPendingApplication;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-xl font-bold">Welcome back, {user?.user_metadata?.full_name || 'User'}!</h2>
        <p className="text-sm text-muted-foreground mt-1">Track your inquiries, saved properties, and stay updated on your requests.</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-primary text-primary-foreground rounded-2xl shadow-lg">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Heart className="h-6 w-6" />
            </div>
            <div>
              <div className="text-3xl font-bold">{savedCount || 0}</div>
              <div className="text-sm opacity-90">Saved Properties</div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl shadow-sm">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
              <Send className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <div className="text-3xl font-bold">{inquiryCount}</div>
              <div className="text-sm text-muted-foreground">My Inquiries</div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl shadow-sm">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <Bell className="h-6 w-6 text-amber-500" />
            </div>
            <div>
              <div className="text-3xl font-bold">{unreadNotifications || 0}</div>
              <div className="text-sm text-muted-foreground">Unread Alerts</div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow rounded-2xl" onClick={() => navigate('/buy')}>
          <CardContent className="p-5 flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-accent/10 flex items-center justify-center">
              <Search className="h-6 w-6 text-accent" />
            </div>
            <div>
              <div className="text-lg font-bold">Browse</div>
              <div className="text-xs text-muted-foreground">Find your dream home</div>
            </div>
            <ArrowRight className="h-4 w-4 ml-auto text-muted-foreground" />
          </CardContent>
        </Card>
      </div>

      {/* Application Status */}
      {applications && applications.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">My Applications</h3>
          {applications.map((app, i) => (
            <ApplicationStatusCard key={i} title={app.title} status={app.status} reviewerNotes={app.reviewerNotes} />
          ))}
        </div>
      )}

      {/* Become A Pro CTA */}
      {showBecomeProCTA && (
        <Card className="rounded-2xl border-0 shadow-sm bg-gradient-to-r from-primary/10 via-primary/5 to-transparent">
          <CardContent className="p-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="h-10 w-10 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
                <BadgeCheck className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="font-semibold">Become a Pro on EthioRealEstates</div>
                <div className="text-xs text-muted-foreground">List properties, get verified, and reach thousands of buyers and renters.</div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate('/agent/apply')}>
                <Briefcase className="h-4 w-4" /> Apply as Agent
              </Button>
              <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate('/agency/apply')}>
                <Building2 className="h-4 w-4" /> Apply as Agency
              </Button>
              <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate('/developer/apply')}>
                <HardHat className="h-4 w-4" /> Apply as Developer
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* My Inquiries / Requests */}
      {myInquiries && myInquiries.length > 0 && (
        <Card className="rounded-2xl border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-blue-500" />
              My Property & Project Inquiries
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 pt-0">
            <div className="space-y-3">
              {myInquiries.map((inquiry: any) => {
                const propertyTitle = inquiry.properties?.title
                  ? (typeof inquiry.properties.title === 'object' ? (inquiry.properties.title as any)?.en || 'Property' : inquiry.properties.title)
                  : null;
                const projectName = inquiry.projects?.name
                  ? (typeof inquiry.projects.name === 'object' ? (inquiry.projects.name as any)?.en || 'Project' : inquiry.projects.name)
                  : null;
                const targetName = propertyTitle || projectName || 'Listing';
                const targetCity = inquiry.properties?.city || inquiry.projects?.city || '';
                const statusInfo = leadStatusConfig[inquiry.status || 'new'] || leadStatusConfig.new;

                return (
                  <div key={inquiry.id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors">
                    <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                      {inquiry.project_id ? <Home className="h-5 w-5 text-blue-500" /> : <Home className="h-5 w-5 text-primary" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{targetName}</div>
                      <div className="text-xs text-muted-foreground">
                        {targetCity && `${targetCity} · `}
                        {formatDistanceToNow(new Date(inquiry.created_at), { addSuffix: true })}
                      </div>
                      {inquiry.message && (
                        <p className="text-xs text-muted-foreground mt-1 truncate italic">"{inquiry.message}"</p>
                      )}
                    </div>
                    <Badge variant={statusInfo.variant} className="shrink-0 text-xs">
                      {statusInfo.label}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Notifications */}
      {recentNotifications && recentNotifications.length > 0 && (
        <Card className="rounded-2xl border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-amber-500" />
                Recent Updates
              </span>
              {(unreadNotifications || 0) > 0 && (
                <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => markAllRead.mutate()} disabled={markAllRead.isPending}>
                  <CheckCheck className="h-3.5 w-3.5" /> Mark all read
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 pt-0">
            <div className="space-y-3">
              {recentNotifications.map((notif: any) => (
                <div key={notif.id} className={`flex items-start gap-3 p-3 rounded-xl transition-colors ${notif.is_read ? 'bg-muted/30' : 'bg-amber-50 dark:bg-amber-950/20 border border-amber-200/50 dark:border-amber-800/30'}`}>
                  <div className={`h-2 w-2 rounded-full mt-2 shrink-0 ${notif.is_read ? 'bg-muted-foreground/30' : 'bg-amber-500'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{notif.title}</div>
                    {notif.message && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notif.message}</p>}
                    <div className="text-xs text-muted-foreground mt-1">
                      {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recently Viewed Properties */}
        {recentViews && recentViews.length > 0 && (
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Eye className="h-4 w-4 text-muted-foreground" />
                Recently Viewed
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5 pt-0">
              <div className="space-y-3">
                {recentViews.map((view: any) => {
                  const title = view.properties?.title
                    ? (typeof view.properties.title === 'object' ? (view.properties.title as any)?.en || 'Property' : view.properties.title)
                    : 'Property';
                  return (
                    <div
                      key={view.id}
                      className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                      onClick={() => navigate(`/property/${view.property_id}`)}
                    >
                      <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                        <Eye className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{title}</div>
                        <div className="text-xs text-muted-foreground">
                          {view.properties?.city} · {view.properties?.property_type}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground shrink-0">
                        {formatDistanceToNow(new Date(view.viewed_at), { addSuffix: true })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Saved Searches */}
        {savedSearches && savedSearches.length > 0 && (
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                Saved Searches
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5 pt-0">
              <div className="space-y-3">
                {savedSearches.map((search: any) => {
                  const filters = search.filters as any;
                  const summary = [
                    filters?.city,
                    filters?.property_type,
                    filters?.listing_type === 'rent' ? 'For Rent' : filters?.listing_type === 'sale' ? 'For Sale' : null,
                    filters?.minPrice || filters?.maxPrice ? `${filters.minPrice?.toLocaleString() || '0'} - ${filters.maxPrice?.toLocaleString() || '∞'} ETB` : null,
                  ].filter(Boolean).join(' · ');

                  return (
                    <div key={search.id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <Search className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/buy?${new URLSearchParams(filters).toString()}`)}>
                        <div className="text-sm font-medium truncate">{search.name || 'Saved Search'}</div>
                        <div className="text-xs text-muted-foreground truncate">{summary || 'All filters'}</div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          size="icon" variant="ghost" className="h-7 w-7"
                          title={search.notify_email ? 'Disable alerts' : 'Enable alerts'}
                          onClick={(e) => { e.stopPropagation(); toggleSearchAlerts.mutate({ id: search.id, notify: !search.notify_email }); }}
                        >
                          <Bell className={`h-3.5 w-3.5 ${search.notify_email ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground'}`} />
                        </Button>
                        <Button
                          size="icon" variant="ghost" className="h-7 w-7"
                          title="Run search"
                          onClick={(e) => { e.stopPropagation(); navigate(`/buy?${new URLSearchParams(filters).toString()}`); }}
                        >
                          <ArrowRight className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive"
                          title="Delete saved search"
                          onClick={(e) => { e.stopPropagation(); deleteSavedSearch.mutate(search.id); }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Recent Saved Properties */}
      {recentFavorites && recentFavorites.length > 0 && (
        <Card className="rounded-2xl border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Heart className="h-4 w-4 text-red-500" />
                Saved Properties
              </span>
              <Button variant="link" size="sm" onClick={() => navigate('/dashboard/saved')}>View All</Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 pt-0">
            <div className="space-y-3">
              {recentFavorites.map((fav: any) => (
                <div key={fav.id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Home className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {typeof fav.properties?.title === 'object' ? (fav.properties.title as any)?.en || 'Property' : 'Property'}
                    </div>
                    <div className="text-xs text-muted-foreground">{fav.properties?.city} · {fav.properties?.property_type}</div>
                  </div>
                  <div className="text-sm font-semibold text-primary">
                    {fav.properties?.price?.toLocaleString()} {fav.properties?.currency || 'ETB'}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow rounded-2xl" onClick={() => navigate('/buy')}>
          <CardContent className="p-5 flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Search className="h-6 w-6 text-primary" />
            </div>
            <div>
              <div className="text-lg font-bold">Buy Property</div>
              <div className="text-xs text-muted-foreground">Browse for sale</div>
            </div>
            <ArrowRight className="h-4 w-4 ml-auto text-muted-foreground" />
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow rounded-2xl" onClick={() => navigate('/rent')}>
          <CardContent className="p-5 flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-green-500/10 flex items-center justify-center">
              <Home className="h-6 w-6 text-green-500" />
            </div>
            <div>
              <div className="text-lg font-bold">Rent Property</div>
              <div className="text-xs text-muted-foreground">Browse for rent</div>
            </div>
            <ArrowRight className="h-4 w-4 ml-auto text-muted-foreground" />
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow rounded-2xl" onClick={() => navigate('/projects')}>
          <CardContent className="p-5 flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-orange-500/10 flex items-center justify-center">
              <Home className="h-6 w-6 text-orange-500" />
            </div>
            <div>
              <div className="text-lg font-bold">View Projects</div>
              <div className="text-xs text-muted-foreground">New developments</div>
            </div>
            <ArrowRight className="h-4 w-4 ml-auto text-muted-foreground" />
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow rounded-2xl" onClick={() => navigate('/mortgage-calculator')}>
          <CardContent className="p-5 flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
              <Calculator className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <div className="text-lg font-bold">Mortgage</div>
              <div className="text-xs text-muted-foreground">Estimate loans</div>
            </div>
            <ArrowRight className="h-4 w-4 ml-auto text-muted-foreground" />
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow rounded-2xl" onClick={() => navigate('/cost-calculator')}>
          <CardContent className="p-5 flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
              <HardHat className="h-6 w-6 text-purple-500" />
            </div>
            <div>
              <div className="text-lg font-bold">Cost Calc</div>
              <div className="text-xs text-muted-foreground">Build estimates</div>
            </div>
            <ArrowRight className="h-4 w-4 ml-auto text-muted-foreground" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
